import { FC, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import _ from 'lodash';
import { Avatar, Button, Checkbox, Popover, Radio, Switch, Tag, Tooltip } from 'antd';
import {
    EditOutlined,
    SyncOutlined,
    InfoCircleOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    LockOutlined,
    UnlockOutlined,
} from '@ant-design/icons';
import { graphXAxisLabelFontSize } from 'components/organism/GrowthAnalysis/SalesAttributionDrilldownChart';
import { colorConstants } from 'config/colorConstants';
import { errorStatuses } from 'components/organism/PricingSimulator/UpdateOrViewPricingSimulator';
import Loader from 'components/molecules/Loader';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { formatNumWithKMBAlt, getAccessIcon } from './helperMethods';
import { ComparisonTableState, Scenario, ScenarioComparison } from 'types/pricingSimulator';

const iconText: any = {
    1: 'Input column',
    2: 'These attributes are only updated once you click on the simulate button',
    3: 'These attributes are auto updated when the dependent attributes are updated',
};

export const pricingSimulatorIndexTableColumns = (
    indexTableFixedColumns: any[],
    additionalColumns: any[] = [],
    projectStatuses: any,
    pathname: string,
    editClickHandler: (rowData: any) => void,
    sortObj: { sortBy: any; sortOrder: any },
    refreshClickHandler: (scenarioId: number, projectId: number) => void,
    enablePrivatePublicToggle: boolean,
) => {
    let result: any[] = [];
    const mergedHeaders = [...indexTableFixedColumns, ...additionalColumns];

    result = mergedHeaders?.map((header: any, index: number) => {
        return {
            title: <p className={`text-[14px] text-purpleFont ${index === 0 ? 'pl-6' : ''}`}>{header?.label}</p>,
            dataIndex: header?.key,
            key: header?.key,
            onCell: (record: any, rowIndex: number) => {
                if (header?.key === 'edit')
                    return {
                        style: {
                            padding: 0,
                        },
                    };
            },
            sortKey: header?.sortKey, //adding a sort key so that on sort click we can read value from this key
            sortOrder: header?.sortKey === sortObj?.sortBy ? sortObj?.sortOrder : null, //condition to highlight sort arrow according to selection
            sorter: header?.sortKey ? true : false,
            sortDirections: header?.key === 'lastUpdated' ? ['ascend', 'descend', 'ascend'] : ['ascend', 'descend'], //always have some sorting in last updated at field
            width: [0, 1].includes(index) ? 180 : header?.key === 'edit' ? 40 : header?.key === 'status' ? 100 : 130,
            render: (columnData: any, rowData: any) => {
                const {
                    isChild,
                    avatarShortName,
                    createdBy,
                    status,
                    isViewMode,
                    projectId,
                    scenarioId,
                    isScenarioLoading,
                    key,
                    errorMessage,
                    allowProjectEdit,
                    isPrivate,
                } = rowData;

                const privatePublicAccessIcon = enablePrivatePublicToggle ? getAccessIcon(isPrivate) : null;
                //showing error message for scenario container when there is no scenario
                if (isChild && errorMessage) {
                    return index == 3 ? <ErrorBox msg={errorMessage} errorStyle={{ padding: 0 }} /> : null;
                }
                //showing loader when scenario is being fetched from backend
                if (isChild && isScenarioLoading) {
                    return index == 3 ? <Loader /> : null;
                }

                //for created by column
                if (header?.key === 'createdBy') {
                    return avatarShortName ? (
                        <div className="flex items-center gap-2">
                            <Avatar
                                className="!text-purpleFont !bg-bgBackground min-w-[32px]"
                                children={<span className="font-xs">{avatarShortName}</span>}
                            />
                            <p className="text-blackAlt break-all">{createdBy}</p>
                        </div>
                    ) : null;
                }

                //for status column
                if (header?.key === 'status') {
                    const itemStatus = projectStatuses?.[columnData];
                    // if parent i.e project hide the status
                    if (!isChild) return null;
                    return (
                        <Tag style={{ borderRadius: 4 }} color={itemStatus?.colorType || 'default'}>
                            {itemStatus?.label}
                        </Tag>
                    );
                }

                //scenario level data
                if (isChild && index === 0) {
                    const url = `${pathname}/${isViewMode ? 'view' : 'update'}/${projectId}/scenario/${scenarioId}`;
                    return (
                        <div className="flex items-center">
                            {/* <span className="mr-2 ">
                                <Checkbox disabled={status === 'expired'} />
                            </span> */}
                            <Link className="text-blackAlt underline" to={url}>
                                {columnData}
                            </Link>
                        </div>
                    );
                }

                //edit icon on project level
                if (!isChild && header?.key === 'edit' && allowProjectEdit) {
                    return (
                        <div
                            onClick={(e) => {
                                //stop propagation so that project wont be expanded
                                e.stopPropagation();
                                editClickHandler(rowData);
                            }}
                            className="flex items-center justify-center"
                        >
                            <Tooltip
                                //  color="white"
                                destroyTooltipOnHide
                                overlayStyle={{ borderRadius: 4 }}
                                overlayInnerStyle={{
                                    fontSize: graphXAxisLabelFontSize,
                                    // color: colorConstants.darkGrey,
                                }}
                                title="Edit Project"
                            >
                                <EditOutlined />
                            </Tooltip>
                        </div>
                    );
                }
                //add refresh icon for invalid scenarios
                if (isChild && header?.key === 'edit' && errorStatuses.includes(status) && !isViewMode) {
                    return (
                        <div
                            onClick={(e) => {
                                //stop propagation so that project wont be expanded
                                e.stopPropagation();
                                refreshClickHandler(scenarioId, projectId);
                                // editClickHandler(rowData);
                            }}
                            className="flex items-center cursor-pointer justify-center"
                        >
                            <Tooltip
                                //  color="white"
                                destroyTooltipOnHide
                                overlayStyle={{ borderRadius: 4 }}
                                overlayInnerStyle={{
                                    fontSize: graphXAxisLabelFontSize,
                                    // color: colorConstants.darkGrey,
                                }}
                                title="Refresh Scenario"
                            >
                                <SyncOutlined />
                            </Tooltip>
                        </div>
                    );
                }
                //project level data
                return (
                    <div className={`text-blackAlt ${index === 0 ? 'font-medium' : ''}`}>
                        {columnData} {header?.key === 'projectName' ? privatePublicAccessIcon : null}
                    </div>
                );
            },
        };
    });

    return result;
};

export const getSingleScenarioTableColumns = (
    data: any[] = [],
    predefinedColumns: any[],
    defaultScenarioTableColumnsLength: number,
    dimensionCheckboxObj: any = {},
    setDimensionCheckboxObj: Function,
    dimSectionWidth: number | string = 150,
    handleInputModalChange: Function,
    handleEditModal: any,
    isDropdownAtLowestLevel: boolean,
    isCheckboxDisabled: boolean,
    showPercentageChange: boolean,
    sortObj: { sortType: any; sortPopupKey: any; sortBy: any; sortOrder: any },
    setSortOption: Function,
    updateScenarioProperty: <K extends keyof Scenario>(key: K, value: Scenario[K]) => void,
    selectedDimensionListValues: string[] = [],
    decimalForNonPercentage: number,
    decimalForPercentage: number,
    lensKpiTypeMapping: any,
    enableMicroInfoForTables: boolean,
) => {
    let result: any[] = [];

    const objKeys = Object.keys(data?.[0] || '{}');
    const lensAttributeColumns =
        (objKeys?.length &&
            objKeys?.slice(defaultScenarioTableColumnsLength).map((item: string, index: number) => ({
                label: item,
                key: item,
                id: data?.[0]?.[item]?.key,
                sortKey: data?.[0]?.[item]?.sortKey,
                iconType: data?.[0]?.[item]?.iconType,
            }))) ||
        [];
    const columns = [...predefinedColumns, ...lensAttributeColumns];

    //checkbox logic
    function handleCheckboxClick(from: 'selectAll' | 'individual', val: boolean, key: string = '', rowData: any = {}) {
        if (isCheckboxDisabled) return;
        let tempCheckboxObj = { ...dimensionCheckboxObj };
        //for select all
        if (from === 'selectAll') {
            if (val) {
                tempCheckboxObj = {
                    ['selectAll']: true,
                };
                data?.forEach((item) => {
                    tempCheckboxObj[item?.key] = {
                        attributeNames: item?.attributeNames,
                        isDeListed: item?.isDeListed,
                        isCompetitorToFollow: item?.isCompetitorToFollow,
                        pk: item?.pk,
                    };
                });
            } else {
                tempCheckboxObj = {};
            }
        } else {
            //for individual checkbox
            if (val) {
                let enableSelectAllBox = true;
                tempCheckboxObj[key] = {
                    attributeNames: rowData?.attributeNames,
                    isDeListed: rowData?.isDeListed,
                    isCompetitorToFollow: rowData?.isCompetitorToFollow,
                    pk: rowData?.pk,
                };
                data?.forEach((item) => {
                    //checking if select all checkbox should be enabled
                    //select all checkbox will be enabled if all individual checkboxes are enabled
                    if (!(item.key in tempCheckboxObj) && enableSelectAllBox) {
                        enableSelectAllBox = false;
                    }
                });
                if (enableSelectAllBox) tempCheckboxObj['selectAll'] = true;
            } else {
                //unselect select all checkbox if one individual checkbox is unselected
                delete tempCheckboxObj['selectAll'];
                delete tempCheckboxObj[key];
            }
        }
        setDimensionCheckboxObj(tempCheckboxObj);
    }

    function applyFunctionHandler(sortObj: any) {
        updateScenarioProperty('sortObject', {
            sortBy: sortObj?.sortBy,
            sortingOrder: sortObj?.sortingOrder,
            sortType: sortObj?.sortType,
        });
        updateScenarioProperty('offset', 0);
        setSortOption(null);
    }
    result = columns?.map((item: any, index: number) => {
        const isColumnEditable = data?.[0]?.[item?.key]?.isEditable;
        return {
            title:
                index === 0 ? (
                    <Checkbox
                        disabled={!isDropdownAtLowestLevel || isCheckboxDisabled}
                        onChange={(val) => handleCheckboxClick('selectAll', val.target.checked)}
                        checked={'selectAll' in dimensionCheckboxObj}
                    />
                ) : (
                    <Popover
                        overlayInnerStyle={{ width: 350, position: 'relative', top: -5 }}
                        content={() => (
                            <SortPopupContainer
                                applyFunctionHandler={applyFunctionHandler}
                                from="scenario"
                                sortObj={sortObj}
                                setSortOption={setSortOption}
                            />
                        )}
                        destroyTooltipOnHide
                        placement="bottom"
                        showArrow={false}
                        trigger={'click'}
                        onOpenChange={(visible) => {
                            if (!visible) setSortOption(null);
                        }}
                        open={sortObj?.sortPopupKey === item?.sortKey}
                    >
                        <div className="flex gap-2 items-center">
                            {item?.iconType > 1 && (
                                <Tooltip
                                    placement="bottomLeft"
                                    title={iconText[item.iconType]}
                                    color={colorConstants.purpleFont}
                                    key={`iconType_${index}`}
                                >
                                    <InfoCircleOutlined />
                                </Tooltip>
                            )}
                            <p
                                className={`font-xs ${
                                    isColumnEditable
                                        ? 'text-purpleFont cursor-pointer hover:text-darkBlueAlt2'
                                        : 'text-fontGreyAlt'
                                } ${index === 0 ? 'pl-6' : ''}`}
                                onClick={(e) => {
                                    if (isColumnEditable) {
                                        e.stopPropagation();
                                        handleInputModalChange({
                                            isOpen: !handleEditModal?.isOpen,
                                            data: { kpiId: item?.id, kpiName: item?.label },
                                            from: 'column',
                                        });
                                    }
                                }}
                            >
                                {item?.label}
                            </p>
                        </div>
                    </Popover>
                ),
            dataIndex: item?.key,
            key: item?.key,
            //hiding sort for now
            sortKey: item?.sortKey, //adding a sort key so that on sort click we can read value from this key
            sortOrder: item?.sortKey === sortObj?.sortBy ? sortObj?.sortOrder : null, //condition to highlight sort arrow according to selection
            sorter: index === 0 ? false : true, //hide sort for checkbox
            sortDirections: ['ascend', 'descend', 'ascend'],
            fixed: index < selectedDimensionListValues.length + 1 ? 'left' : false,
            width:
                index === 0
                    ? 50
                    : index <= selectedDimensionListValues.length &&
                      columns.length > selectedDimensionListValues?.length + 1
                    ? Math.abs((Number(dimSectionWidth) - 50) / selectedDimensionListValues.length) - 4
                    : 'auto',
            onCell: (record: any, rowIndex: number) => {
                const isFirstRow = rowIndex === 0;

                if (isFirstRow && enableMicroInfoForTables) {
                    return {
                        style: {
                            padding: '2px 16px',
                            borderRight:
                                index !== 0 && index < selectedDimensionListValues.length
                                    ? ''
                                    : `1px solid ${colorConstants.greyAlt3}`,
                        },
                    };
                }
                if (index === 0) {
                    return {
                        style: {
                            borderRight: `1px solid ${colorConstants.greyAlt3}`,
                        },
                    };
                }
                //delist background and opacity
                if (record?.isDeListed) {
                    return {
                        style: {
                            padding: [1, 2].includes(index) ? 0 : 'auto', //removing padding for 1-3 columns because of click event
                            cursor: 'not-allowed',
                            pointerEvents: 'none',
                            background: colorConstants.greyAlt9,
                            borderRight:
                                index === 2
                                    ? `1px solid ${colorConstants.greyAlt12}`
                                    : index > 2
                                    ? `1px solid ${colorConstants.greyAlt6}`
                                    : '',
                        },
                    };
                }
                //background for 1st and 2nd columns - dimension columns
                if (index !== 0 && index <= selectedDimensionListValues.length) {
                    return {
                        style: {
                            padding: 0, //removing padding here because of click event
                            cursor: isDropdownAtLowestLevel ? 'pointer' : 'default',
                            background: colorConstants.greyAlt9,
                            borderRight:
                                index === selectedDimensionListValues.length
                                    ? `1px solid ${colorConstants.greyAlt12}`
                                    : '',
                        },
                    };
                }
                return {
                    style: {
                        pointerEvents: isColumnEditable ? 'auto' : 'none',
                        background: isColumnEditable ? 'unset' : colorConstants.greyAlt9, //greying out cell if not editable
                        borderRight: `1px solid ${colorConstants.greyAlt6}`,
                    },
                };
            },
            render: (columnData: any, rowData: any) => {
                const isDeListed = rowData?.isDeListed;
                const isCompetitorToFollow = rowData?.isCompetitorToFollow;
                const isSubHeader = rowData?.key === 'simulator-info-header';
                //1st col checkbox
                if (index === 0) {
                    if (isSubHeader) return <></>;
                    return (
                        <Checkbox
                            disabled={!isDropdownAtLowestLevel || isCheckboxDisabled}
                            onChange={(val) =>
                                handleCheckboxClick('individual', val.target.checked, rowData?.key, rowData)
                            }
                            checked={'selectAll' in dimensionCheckboxObj || rowData?.key in dimensionCheckboxObj}
                        />
                    );
                }
                //dim1 and dim2
                if (index !== 0 && index <= selectedDimensionListValues.length) {
                    if (isSubHeader) return <></>;
                    return (
                        <div
                            className={`font-xs ${isDeListed ? 'opacity-50' : ''}`}
                            style={{ padding: 16 }}
                            onClick={() => {
                                isDropdownAtLowestLevel &&
                                    handleCheckboxClick(
                                        'individual',
                                        !(rowData?.key in dimensionCheckboxObj),
                                        rowData?.key,
                                        rowData,
                                    );
                            }}
                        >
                            {index === 2 ? (
                                <span className=" px-3 py-1 rounded">{columnData}</span>
                            ) : (
                                <div className="flex items-center">
                                    <span>{columnData}</span>
                                    {/* competitor symbol */}
                                    {isCompetitorToFollow && (
                                        <div className=" ml-2 rounded-full px-2 py-1 bg-creamBackground">C</div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                }
                //use undefined instead of null as isNaN(null) is false
                const leftValue = columnData?.output ?? columnData?.input ?? columnData?.actual; // priority -> output > input > actual
                const formattedLeftValue = isNaN(leftValue)
                    ? leftValue
                    : formatNumWithKMBAlt(leftValue, columnData?.allowedDecimals, columnData?.allowedDecimals); //adding K,M,B

                //check for boolean
                const displayValue =
                    typeof columnData?.displayValue === 'boolean'
                        ? String(columnData?.displayValue)
                        : isNaN(columnData?.displayValue)
                        ? columnData?.displayValue
                        : formatNumWithKMBAlt(
                              columnData?.displayValue,
                              columnData?.allowedDecimals,
                              columnData?.allowedDecimals,
                          );

                if (isSubHeader) {
                    if (lensKpiTypeMapping[item.id] === 4)
                        return <div className="text-left font-medium text-blackAlt ">Original</div>;
                    else
                        return (
                            <div className="flex justify-between items-center max-w-[204px] font-medium">
                                <span className="font-xs basis-2/5 text-blackAlt  ">Updated</span>
                                <span className="font-xs basis-1/5 text-blackAlt ">
                                    {showPercentageChange ? '%Chg' : 'Abs.Chg'}
                                </span>
                                <span className="font-xs basis-2/5 text-blackAlt text-right ">Original</span>
                            </div>
                        );
                }

                const percentageValue = columnData?.perChange;
                const formattedPercentageValue = isNaN(percentageValue)
                    ? percentageValue
                    : formatNumWithKMBAlt(percentageValue, columnData?.allowedDecimals); //adding K,M,B

                const absValue = columnData?.absChange;
                const formattedAbsoluteValue = isNaN(absValue)
                    ? absValue
                    : formatNumWithKMBAlt(absValue, columnData?.allowedDecimals, columnData?.allowedDecimals); //adding K,M,B

                const rightValue = columnData?.output ?? !isNaN(columnData?.input) ? columnData?.actual : undefined; //only add right value if output or input is present in left value
                const formattedRightValue = isNaN(rightValue)
                    ? rightValue
                    : formatNumWithKMBAlt(rightValue, columnData?.allowedDecimals, columnData?.allowedDecimals); //adding K,M,B
                const percentageValueColor =
                    !_.isNil(percentageValue) && percentageValue === 0
                        ? colorConstants.greyAlt14
                        : percentageValue > 0
                        ? colorConstants.limeGreen
                        : colorConstants.redAlt;
                const isLeftValueInput = _.isNil(columnData?.output) && !_.isNil(columnData?.input);

                //for lenses attributes
                return displayValue ? (
                    <div className="flex justify-start"> {displayValue}</div>
                ) : (
                    <div className="flex justify-between items-center max-w-[204px]">
                        {/* 1. new value */}
                        <span
                            onClick={() => {
                                if (isLeftValueInput && !isDeListed) {
                                    handleInputModalChange({
                                        isOpen: !handleEditModal?.isOpen,
                                        data: {
                                            cellId: rowData?.pk,
                                            attributeNames: rowData?.attributeNames,
                                            kpiId: item?.id,
                                            kpiName: item?.label,
                                        },
                                        from: 'cell',
                                    });
                                }
                            }}
                            className={`font-xs basis-2/5 ${
                                isLeftValueInput ? 'text-darkBlueAlt1 underline cursor-pointer' : 'text-blackAlt'
                            } ${isDeListed ? 'opacity-50' : ''}`}
                        >
                            {formattedLeftValue ? `${columnData.pre}${formattedLeftValue}${columnData.post}` : ''}
                        </span>
                        {/* 2. abs / percentage change */}
                        <span
                            className={`font-xs basis-1/5 ${isDeListed ? 'opacity-50' : ''}`}
                            style={{ color: percentageValueColor }}
                        >
                            {showPercentageChange
                                ? `${formattedPercentageValue ? formattedPercentageValue + '%' : ''}`
                                : `${formattedAbsoluteValue ? formattedAbsoluteValue : ''}`}
                        </span>
                        {/* 3. old value */}
                        <div
                            className={`basis-2/5 text-right font-xs text-fontGreyAlt ${
                                isDeListed ? 'opacity-50' : ''
                            }`}
                        >
                            {formattedRightValue ? `${columnData.pre}${formattedRightValue}${columnData.post}` : ''}
                        </div>
                    </div>
                );
            },
        };
    });
    return result;
};

type Props = {
    sortObj: { sortType: any; sortPopupKey: any; sortBy: any; sortOrder: any };
    setSortOption: Function;
    from: 'scenario' | 'comparison';
    applyFunctionHandler: Function;
};

//sorting popup
const SortPopupContainer: FC<Props> = ({ from = 'scenario', sortObj, setSortOption, applyFunctionHandler }) => {
    function isSortOrderDescending() {
        return sortObj.sortOrder === 'descend' && sortObj.sortBy === sortObj.sortPopupKey;
    }
    function fetchSelectedSortType() {
        return sortObj.sortBy === sortObj.sortPopupKey ? sortObj.sortType : '';
    }
    const [isDescendingOrder, setIsDescendingOrder] = useState(isSortOrderDescending());
    const [selectedSortType, setSelectedSortType] = useState(fetchSelectedSortType());
    //called when clicked on apply button
    function applyHandler() {
        applyFunctionHandler({
            sortBy: sortObj?.sortPopupKey,
            sortingOrder: isDescendingOrder ? 'desc' : 'asc',
            sortType: selectedSortType,
        });
    }

    //remove original for comparison
    const updatedSortOptions = useMemo(() => {
        let temp = [...sortOptions];
        if (from === 'comparison') {
            temp.splice(2, 1);
        }
        return temp;
    }, [from]);
    return (
        // stopping propagation so that sorter onchange wont be called
        <div onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between gap-5 mb-2 items-center">
                <p className="text-promoColour pl-2 font-xxs">Sorting Option</p>
                <div>
                    <span className="text-promoColour font-xxs mr-1">Asc</span>
                    <Switch
                        checked={isDescendingOrder}
                        onChange={(checked) => {
                            setIsDescendingOrder(checked);
                        }}
                    />
                    <span className="text-promoColour font-xxs ml-1">Desc</span>
                </div>
            </div>
            <div className="text-blackAlt p-3 rounded border border-backgroundGreyAlt6 my-1 font-xs flex flex-col cursor-pointer">
                <Radio.Group
                    onChange={(e) => setSelectedSortType(e.target.value)}
                    value={selectedSortType}
                    style={{ display: 'flex', flexDirection: 'column', marginLeft: '10px' }}
                >
                    {updatedSortOptions.map((item) => {
                        return (
                            <Radio
                                // disabled={data?.from !== 'cell'}
                                style={{ marginBlock: 2, fontSize: 12, color: colorConstants.blackAlt1 }}
                                value={item?.value}
                            >
                                {item?.label}
                            </Radio>
                        );
                    })}
                </Radio.Group>
            </div>
            <div className="flex gap-2 mt-2 justify-end">
                <Button style={{ borderRadius: 6 }} onClick={() => setSortOption(null)}>
                    Cancel
                </Button>
                <Button style={{ borderRadius: 6 }} type="primary" disabled={!selectedSortType} onClick={applyHandler}>
                    Apply
                </Button>
            </div>
        </div>
    );
};

const sortOptions = [
    { label: 'Sort by Percentage change', value: 'percentage_change' },
    { label: 'Sort by Incremental change', value: 'incremental_change' },
    { label: 'Sort by Original Value', value: 'actual' },
    { label: 'Sort by Final Value', value: 'new' },
];

// will convert set into array and group them by dim1 and dim2 respectively
export function transformFilterObjectWithDimensionsInfo(input: any, scenarioFilterKeys: any[]) {
    const output: any = {};

    scenarioFilterKeys.forEach(({ value, dimension }) => {
        if (input[value]) {
            if (!output[dimension]) {
                output[dimension] = {};
            }

            const filter = input[value];
            const filterKey = Object.keys(filter)[0];

            // Exclude if the filterKey or its value is undefined or if its empty set
            if (filterKey && filter[filterKey] && filter[filterKey].size) {
                output[dimension][value] = {
                    [filterKey]: Array.from(filter[filterKey]),
                };
            }
        }
    });

    return output;
}

export const getAnalyticsTableColumns = (
    indexTableFixedColumns: any[],
    loggedInUser: string,
    sortObj: { sortBy: any; sortOrder: any },
    enablePrivatePublicToggle: boolean,
) => {
    let result: any[] = [];
    const mergedHeaders = [...indexTableFixedColumns];

    result = mergedHeaders?.map((header: any, index: number) => {
        return {
            title: <p className={`text-[14px] text-purpleFont }`}>{header?.label}</p>,
            dataIndex: header?.key,
            key: header?.key,
            onCell: (record: any, rowIndex: number) => {
                if (header?.key === 'edit')
                    return {
                        style: {
                            padding: 0,
                        },
                    };
            },
            sortKey: header?.sortKey, //adding a sort key so that on sort click we can read value from this key
            sortOrder: header?.sortKey === sortObj?.sortBy ? sortObj?.sortOrder : null, //condition to highlight sort arrow according to selection
            sorter: header?.sortKey ? true : false,
            sortDirections: header?.key === 'lastUpdated' ? ['ascend', 'descend', 'ascend'] : ['ascend', 'descend'], //always have some sorting in last updated at field
            width: [0, 1, 2].includes(index) ? 180 : 160,
            render: (columnData: any, rowData: any) => {
                const { avatarShortName, createdBy, isPrivate } = rowData;

                const reportId = rowData?.key || 0;
                const mode = loggedInUser === rowData.createdBy ? 'update' : 'view';
                const privatePublicAccessIcon = enablePrivatePublicToggle ? getAccessIcon(isPrivate) : null;

                // scenario name column
                if (index === 0) {
                    return (
                        <Link
                            to={`/pricing/recommendation/pricing-analytics/${mode}/${reportId}`}
                            className=" cursor-pointer text-purpleFont  hover:text-purpleFont  font-medium py-1  text-start"
                        >
                            {columnData} {privatePublicAccessIcon}
                        </Link>
                    );
                }

                //for created by column
                if (header?.key === 'createdBy') {
                    return avatarShortName ? (
                        <div className="flex items-center gap-2">
                            <Avatar
                                className="!text-purpleFont !bg-bgBackground min-w-[32px]"
                                children={<span className="font-xs">{avatarShortName}</span>}
                            />
                            <p className="text-blackAlt break-all">{createdBy}</p>
                        </div>
                    ) : null;
                }

                //project level data
                return <div className={`text-blackAlt ${index === 0 ? 'font-medium' : ''}`}>{columnData}</div>;
            },
        };
    });

    return result;
};

export const pricingAnalyticsBaseScenarioTableColumns = (
    indexTableFixedColumns: any[],
    sortObj: { sortBy: any; sortOrder: any },
    selectedScenarioIds: any[],
    projectStatuses: any,
    enablePrivatePublicToggle: boolean,
) => {
    let result: any[] = [];
    const mergedHeaders = [...indexTableFixedColumns];

    result = mergedHeaders?.map((header: any, index: number) => {
        return {
            title: <p className={`text-[14px] text-purpleFont ${index === 0 ? 'pl-6' : ''}`}>{header?.label}</p>,
            dataIndex: header?.key,
            key: header?.key,
            onCell: (record: any, rowIndex: number) => {
                if (record?.isChild) {
                    return {
                        style: {
                            cursor: 'pointer',
                            background: selectedScenarioIds.includes(record?.scenarioId) ? colorConstants.grey : '',
                        },
                    };
                }
            },
            sortKey: header?.sortKey, //adding a sort key so that on sort click we can read value from this key
            sortOrder: header?.sortKey === sortObj?.sortBy ? sortObj?.sortOrder : null, //condition to highlight sort arrow according to selection
            sorter: header?.sortKey ? true : false,
            sortDirections: header?.key === 'lastUpdated' ? ['ascend', 'descend', 'ascend'] : ['ascend', 'descend'], //always have some sorting in last updated at field
            width: [0, 1].includes(index) ? 180 : 80,
            render: (columnData: any, rowData: any) => {
                const { isChild, avatarShortName, createdBy, scenarioId, isScenarioLoading, errorMessage, isPrivate } =
                    rowData;
                const privatePublicAccessIcon = enablePrivatePublicToggle ? getAccessIcon(isPrivate) : null;
                //showing error message for scenario container when there is no scenario
                if (isChild && errorMessage) {
                    return index == 0 ? <ErrorBox msg={errorMessage} errorStyle={{ padding: 0 }} /> : null;
                }
                //showing loader when scenario is being fetched from backend
                if (isChild && isScenarioLoading) {
                    return index == 1 ? <Loader style={{ justifyContent: 'flex-start', marginLeft: 30 }} /> : null;
                }

                //for created by column
                if (header?.key === 'createdBy') {
                    return avatarShortName ? (
                        <div className="flex items-center gap-2">
                            <Avatar
                                className="!text-purpleFont !bg-bgBackground min-w-[32px]"
                                children={<span className="font-xs">{avatarShortName}</span>}
                            />
                            <p className="text-blackAlt break-all">{createdBy}</p>
                        </div>
                    ) : null;
                }
                //for status column
                if (header?.key === 'status') {
                    const itemStatus = projectStatuses?.[columnData];
                    return (
                        <Tag style={{ borderRadius: 4 }} color={itemStatus?.colorType || 'default'}>
                            {itemStatus?.label}
                        </Tag>
                    );
                }
                //scenario level data
                if (isChild && index === 0) {
                    return (
                        <div className="flex items-center">
                            <span className="mr-2 ">
                                <Checkbox checked={selectedScenarioIds.includes(scenarioId)} />
                            </span>
                            <p className="text-blackAlt underline">{columnData}</p>
                        </div>
                    );
                }
                //project level data
                return (
                    <div className={`text-blackAlt ${index === 0 ? 'font-medium' : ''}`}>
                        {columnData}
                        {index === 0 ? privatePublicAccessIcon : null}
                    </div>
                );
            },
        };
    });

    return result;
};

export const getComparisonTableColumns = (
    tableData: ComparisonTableState,
    predefinedColumns: any[],
    defaultComparisonTableColumnsLength: number,
    dimSectionWidth: number | string = 150,
    showOldValue: boolean,
    sortObj: { sortType: any; sortPopupKey: any; sortBy: any; sortOrder: any },
    setSortOption: Function,
    updateComparisonProperty: <K extends keyof ScenarioComparison>(key: K, value: ScenarioComparison[K]) => void,
    selectedDimensionListValues: string[] = [],
    decimalForNonPercentage: number,
    decimalForPercentage: number,
) => {
    let result: any[] = [];
    const data = tableData?.data ?? [];
    const objKeys = Object.keys(data?.[0] || '{}');
    const lensAttributeColumns =
        (objKeys?.length &&
            objKeys?.slice(defaultComparisonTableColumnsLength).map((item: string, index: number) => ({
                label: data?.[0]?.[item]?.label,
                key: item,
                id: data?.[0]?.[item]?.key,
                sortKey: data?.[0]?.[item]?.sortKey,
            }))) ||
        [];
    const columns = [...predefinedColumns, ...lensAttributeColumns];

    function applyFunctionHandler(sortObj: any) {
        updateComparisonProperty('tableState', {
            ...tableData,
            sortObject: {
                sortBy: sortObj?.sortBy,
                sortingOrder: sortObj?.sortingOrder,
                sortType: sortObj?.sortType,
            },
            offset: 0,
        });
        setSortOption(null);
    }

    result = columns?.map((item: any, index: number) => {
        return {
            title: (
                <Popover
                    overlayInnerStyle={{ width: 350, position: 'relative', top: -5 }}
                    content={() => (
                        <SortPopupContainer
                            applyFunctionHandler={applyFunctionHandler}
                            from="comparison"
                            sortObj={sortObj}
                            setSortOption={setSortOption}
                        />
                    )}
                    destroyTooltipOnHide
                    placement="bottom"
                    showArrow={false}
                    trigger={'click'}
                    onOpenChange={(visible) => {
                        if (!visible) setSortOption(null);
                    }}
                    open={sortObj?.sortPopupKey === item?.sortKey}
                >
                    <div className="flex gap-2 items-center">
                        <p className={`font-xs ${'text-purpleFont cursor-pointer hover:text-darkBlueAlt2'}`}>
                            {item?.label}
                        </p>
                    </div>
                </Popover>
            ),
            dataIndex: item?.key,
            key: item?.key,
            //hiding sort for now
            sortKey: item?.sortKey, //adding a sort key so that on sort click we can read value from this key
            sortOrder: item?.sortKey === sortObj?.sortBy ? sortObj?.sortOrder : null, //condition to highlight sort arrow according to selection
            sorter: true,
            sortDirections: ['ascend', 'descend', 'ascend'],
            fixed: index < selectedDimensionListValues.length ? 'left' : false,
            width:
                index < selectedDimensionListValues.length && columns.length > selectedDimensionListValues?.length
                    ? Number(dimSectionWidth) / selectedDimensionListValues.length
                    : 'auto',

            onCell: (record: any, rowIndex: number) => {
                if (index < selectedDimensionListValues.length) {
                    return {
                        style: {
                            borderRight:
                                index === selectedDimensionListValues.length - 1
                                    ? `1px solid ${colorConstants.greyAlt16}`
                                    : '',
                        },
                    };
                }
                return {
                    style: {
                        padding: 8,
                        borderRight: `1px solid ${colorConstants.greyAlt6}`,
                    },
                };
            },
            render: (columnData: any, rowData: any) => {
                //dim1 and dim2
                if (index < selectedDimensionListValues.length) {
                    return (
                        <div className={`font-xs`}>
                            <span>{columnData}</span>
                        </div>
                    );
                }
                //use undefined instead of null as isNaN(null) is false
                const newValue = columnData?.newValue;
                const formattedNewValue = isNaN(newValue)
                    ? newValue
                    : formatNumWithKMBAlt(newValue, decimalForNonPercentage, decimalForNonPercentage); //adding K,M,B

                const oldValue = columnData?.oldValue;
                const formattedOldValue = isNaN(oldValue)
                    ? oldValue
                    : formatNumWithKMBAlt(oldValue, decimalForNonPercentage, decimalForNonPercentage); //adding K,M,B

                const percentageValue = columnData?.perChange;
                const formattedPercentageValue = isNaN(percentageValue)
                    ? percentageValue
                    : formatNumWithKMBAlt(percentageValue, decimalForPercentage); //adding K,M,B

                const absValue = columnData?.absChange;
                const formattedAbsoluteValue = isNaN(absValue)
                    ? absValue
                    : formatNumWithKMBAlt(absValue, decimalForNonPercentage, decimalForNonPercentage); //adding K,M,B

                const percentageValueColor =
                    !_.isNil(percentageValue) && percentageValue === 0
                        ? colorConstants.greyAlt14
                        : percentageValue > 0
                        ? colorConstants.limeGreen
                        : colorConstants.redAlt;

                return (
                    <div className="flex justify-between max-w-[204px] items-center">
                        <p className={`font-xs pl-2 text-blackAlt`}>
                            {showOldValue
                                ? formattedOldValue
                                    ? `${columnData.pre}${formattedOldValue}${columnData.post}`
                                    : ''
                                : formattedNewValue
                                ? `${columnData.pre}${formattedNewValue}${columnData.post}`
                                : ''}
                        </p>
                        <p className={`font-xs`} style={{ color: percentageValueColor }}>
                            {`${formattedAbsoluteValue ? formattedAbsoluteValue : ''}`}

                            {/* <span>
                                    {!_.isNil(percentageValue) &&
                                        percentageValue !== 0 &&
                                        (percentageValue > 0 ? (
                                            <ArrowUpOutlined
                                                style={{ color: percentageValueColor, position: 'relative', top: -3 }}
                                            />
                                        ) : (
                                            <ArrowDownOutlined
                                                style={{ color: percentageValueColor, position: 'relative', top: -3 }}
                                            />
                                        ))}
                                </span> */}
                        </p>
                        <p className={`font-xs`} style={{ color: percentageValueColor }}>
                            {`${formattedPercentageValue ? formattedPercentageValue + '%' : ''}`}
                        </p>
                    </div>
                );
            },
        };
    });
    return result;
};
